import { useState, useEffect, useRef } from "react";
import {
  Space,
  Spin,
  message,
  Menu,
  Input,
  List,
  Card,
  Button,
  Flex,
  Image,
  Pagination,
  Badge,
  Select,
  Grid,
  Drawer,
  Typography,
} from "antd";
import { AppstoreOutlined, LoadingOutlined } from "@ant-design/icons";

import { useNavigate, useLocation } from "react-router-dom";

import PageTitle from "../../../Components/PageTitle/pageTitle";
import publicDataServices from "../../../APIs/publicDataServices";

import { settings } from "../../../Services/apiSetting";
import { toSlug } from "../../../Components/Common/helpers";

const { Search } = Input;

const { useBreakpoint } = Grid;

const Products = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const navigate = useNavigate();
  const location = useLocation();

  const [messageApi, contextHolder] = message.useMessage();

  const [open, setOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [products, setProducts] = useState([]);

  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  const [combinedCategories, setCombinedCategories] = useState([]);

  const containerRef = useRef(null);
  const [pageSizeVar, setPageSizeVar] = useState(20);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: pageSizeVar,
    total: 0,
  });

  const [searchValue, setSearchValue] = useState("");
  const [sortValue, setSortValue] = useState("date_asc");

  //#region Responsive page size calculation
  const CARD_MIN = 220;
  const GRID_GAP = 16;

  const getRowsForColumns = (cols) => {
    if (cols <= 2) return 6;
    if (cols === 3) return 5;
    if (cols === 4) return 4;
    if (cols === 5) return 4; // ✅ more data on 5 cols
    return 4; // 6+ cols also 4 (clamp controls max)
  };

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const calcPageSize = (containerWidth) => {
    const cols = Math.max(
      1,
      Math.floor((containerWidth + GRID_GAP) / (CARD_MIN + GRID_GAP))
    );
    const rows = getRowsForColumns(cols);
    return clamp(cols * rows, 12, 36); // min 12, max 36
  };

  useEffect(() => {
    const update = () => {
      const w = containerRef.current?.offsetWidth;
      if (!w) return;

      const next = calcPageSize(w);
      setPageSizeVar((prev) => (prev === next ? prev : next));
    };

    update();

    const onResize = () => requestAnimationFrame(update);

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, []);
  //#endregion

  useEffect(() => {
    getCombinedCategories();
  }, []);

  const parseMenuKey = (key) => {
    const [type, idStr] = String(key).split("-");
    const id = parseInt(idStr, 10);

    if (!["cat", "sub"].includes(type) || Number.isNaN(id)) return null;
    return { type, id };
  };

  const findByKey = (categories, key) => {
    const parsed = parseMenuKey(key);
    if (!parsed) return null;

    const { type, id } = parsed;

    if (type === "cat") {
      const category = categories.find((c) => c.Id === id);
      return category ? { category, subCategory: null } : null;
    }

    // type === "sub"
    for (const category of categories) {
      const sub = (category.SubCategories || []).find((s) => s.Id === id);
      if (sub) return { category, subCategory: sub };
    }

    return null;
  };

  useEffect(() => {
    if (!combinedCategories?.length) return;

    const selectedKeyFromState = location.state?.category?.SelectedKey;

    if (selectedKeyFromState) {
      const found = findByKey(
        combinedCategories,
        location.state?.category?.SelectedKey
      );

      if (found) {
        setOpenKeys([`cat-${found.category.Id}`]);
        setSelectedKey(location.state?.category?.SelectedKey);

        return;
      }
    }

    const catId = location.state?.category?.Id ?? combinedCategories[0]?.Id;

    const cat = combinedCategories.find((c) => c.Id === catId);

    if (!cat) return;

    setOpenKeys([`cat-${catId}`]);
    setSelectedKey(`cat-${catId}`);
  }, [combinedCategories]);

  const handleOpenChange = (keys) => {
    if (keys.length === 0) return;

    const latestKey = keys.find((k) => !openKeys.includes(k));

    if (!latestKey) return;

    setOpenKeys([latestKey]);
  };

  const getCombinedCategories = async () => {
    try {
      const response = await publicDataServices.getCombinedCategories();

      if (response.data.statusCode === 200) {
        let combinedCats = JSON.parse(response.data.data);

        // add All tag in front
        combinedCats.unshift({
          Id: 0,
          CategoryName: "All",
        });

        setCombinedCategories(combinedCats);

        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching combined categories:", error);

      messageApi.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to fetch categories"
      );
    }
  };

  useEffect(() => {
    if (selectedKey == null) return;

    const page = location.state?.currentPage ?? 1;

    getProducts(page, pageSizeVar, sortValue);
  }, [selectedKey, pageSizeVar, sortValue, location.state?.currentPage]);

  const getProducts = async (
    page = location.state?.currentPage ?? 1,
    pageSize = pageSizeVar,
    sort = sortValue
  ) => {
    try {
      // console.log("Fetching products with:", {
      //   page,
      //   pageSize,
      //   sort,
      //   searchValue,
      //   selectedKey,
      // });

      setProducts([]);
      // setIsLoading(true);
      setIsPaginationLoading(true);

      const skip = (page - 1) * pageSize;
      const take = pageSize;

      const response = await publicDataServices.getProducts(
        skip,
        take,
        searchValue,
        selectedKey,
        sort
      );

      // console.log("getProducts response:", JSON.parse(response.data.data));

      if (response.data.statusCode === 200) {
        const data = JSON.parse(response.data.data);

        setProducts(data.products);

        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: data.total,
        }));
      }
    } catch (error) {
      console.error("Error fetching products:", error);

      messageApi.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to fetch products"
      );
    } finally {
      setIsLoading(false);
      setIsPaginationLoading(false);
    }
  };

  const updateRouteState = (patch) => {
    navigate(location.pathname, {
      replace: true,
      state: {
        ...(location.state || {}),
        ...patch,
      },
    });
  };

  return (
    <Space direction="vertical" className="page-container">
      {isLoading && (
        <Spin
          spinning={isLoading}
          indicator={<LoadingOutlined spin />}
          fullscreen
        />
      )}

      {contextHolder}

      <PageTitle
        title="Explore Our Product Range"
        subTitle="Browse our complete catalog and find what suits your renovation needs."
        buttonText=""
      />

      {(isMobile || isTablet) && (
        <>
          <Button
            block
            icon={<AppstoreOutlined />}
            onClick={() => setOpen(true)}
            style={{ marginTop: isMobile ? 10 : undefined }}
          >
            Browse Categories
          </Button>

          <Drawer
            title="Product Categories"
            placement="left"
            onClose={() => setOpen(false)}
            open={open}
            styles={{
              body: {
                paddingLeft: 0,
                paddingRight: 50,
                paddingBottom: 0,
              },
            }}
          >
            <Menu
              mode="inline"
              openKeys={openKeys}
              selectedKeys={[selectedKey]}
              onOpenChange={handleOpenChange}
              onClick={({ key }) => {
                setSelectedKey(key);

                if (key === "cat-0") setOpenKeys([]);

                let selectedCategoryId = key.split("-")[1];
                let parseIntSelectedCategoryId = parseInt(selectedCategoryId);

                updateRouteState({
                  category: {
                    Id: parseIntSelectedCategoryId,
                    SelectedKey: key,
                  },
                  currentPage: 1,
                });

                setOpen(false); // close drawer
              }}
              style={{
                border: 0,
              }}
              items={combinedCategories.map((cat) => {
                const hasSubs = cat.SubCategories?.length > 0;
                const catKey = `cat-${cat.Id}`;
                const isCatSelected = selectedKey === catKey;

                return hasSubs
                  ? {
                      key: catKey,
                      label: (
                        <span
                          style={{
                            backgroundColor: isCatSelected
                              ? "#FFDC00"
                              : undefined,
                            color: isCatSelected ? "#000" : undefined,
                            fontWeight: isCatSelected ? 500 : undefined,
                            display: "block",
                            padding: "5px 12px",
                            borderRadius: 10,
                          }}
                        >
                          {cat.CategoryName}
                        </span>
                      ),
                      onTitleClick: () => {
                        setSelectedKey(catKey);

                        let selectedCategoryId = catKey.split("-")[1];
                        let parseIntSelectedCategoryId =
                          parseInt(selectedCategoryId);

                        updateRouteState({
                          category: {
                            Id: parseIntSelectedCategoryId,
                            SelectedKey: catKey,
                          },
                          currentPage: 1,
                        });
                      },
                      children: cat.SubCategories.map((sub) => {
                        const subKey = `sub-${sub.Id}`;
                        const isSubSelected = selectedKey === subKey;

                        return {
                          key: subKey,
                          label: (
                            <span
                              style={{
                                backgroundColor: isSubSelected
                                  ? "#FFDC00"
                                  : undefined,
                                color: isSubSelected ? "#000" : undefined,
                                fontWeight: isSubSelected ? 500 : undefined,
                                display: "block",
                                padding: "5px 12px",
                                borderRadius: 10,
                              }}
                            >
                              {sub.SubCategoryName}
                            </span>
                          ),
                        };
                      }),
                    }
                  : {
                      key: catKey,
                      label: (
                        <span
                          style={{
                            backgroundColor: isCatSelected
                              ? "#FFDC00"
                              : undefined,
                            color: isCatSelected ? "#000" : undefined,
                            fontWeight: isCatSelected ? 500 : undefined,
                            display: "block",
                            padding: "5px 12px",
                            borderRadius: 10,
                          }}
                        >
                          {cat.CategoryName}
                        </span>
                      ),
                    };
              })}
            />

            {/* <Collapse accordion>
              {combinedCategories.map((cat) => {
                const catKey = `cat-${cat.Id}`;
                const hasSubs = cat.SubCategories?.length > 0;

                return (
                  <Panel header={cat.CategoryName} key={catKey}>
                    {hasSubs &&
                      cat.SubCategories.map((sub) => {
                        const subKey = `sub-${sub.Id}`;
                        return (
                          <div
                            key={subKey}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              fontWeight: selectedKey === subKey ? 600 : 400,
                              backgroundColor:
                                selectedKey === subKey ? "#FFDC00" : undefined,
                            }}
                            onClick={() => {
                              setSelectedKey(subKey);
                              setOpen(false); // close drawer
                            }}
                          >
                            {sub.SubCategoryName}
                          </div>
                        );
                      })}
                  </Panel>
                );
              })}
            </Collapse> */}
          </Drawer>
        </>
      )}

      <Flex
        style={{
          width: "100%",
        }}
        gap={20}
      >
        {!isMobile && !isTablet && (
          <Menu
            mode="inline"
            openKeys={openKeys}
            selectedKeys={[selectedKey]}
            onOpenChange={handleOpenChange}
            onClick={({ key }) => {
              setSelectedKey(key);

              if (key === "cat-0") setOpenKeys([]);

              let selectedCategoryId = key.split("-")[1];
              let parseIntSelectedCategoryId = parseInt(selectedCategoryId);

              updateRouteState({
                category: {
                  Id: parseIntSelectedCategoryId,
                  SelectedKey: key,
                },
                currentPage: 1,
              });
            }}
            items={combinedCategories.map((cat) => {
              const hasSubs = cat.SubCategories?.length > 0;
              const catKey = `cat-${cat.Id}`;
              const isCatSelected = selectedKey === catKey;

              return hasSubs
                ? {
                    key: catKey,
                    label: (
                      <span
                        style={{
                          backgroundColor: isCatSelected
                            ? "#FFDC00"
                            : undefined,
                          color: isCatSelected ? "#000" : undefined,
                          fontWeight: isCatSelected ? 500 : undefined,
                          display: "block",
                          padding: "5px 12px",
                          borderRadius: 10,
                        }}
                      >
                        {cat.CategoryName}
                      </span>
                    ),
                    onTitleClick: () => {
                      setSelectedKey(catKey);

                      let selectedCategoryId = catKey.split("-")[1];
                      let parseIntSelectedCategoryId =
                        parseInt(selectedCategoryId);

                      updateRouteState({
                        category: {
                          Id: parseIntSelectedCategoryId,
                          SelectedKey: catKey,
                        },
                        currentPage: 1,
                      });
                    },
                    children: cat.SubCategories.map((sub) => {
                      const subKey = `sub-${sub.Id}`;
                      const isSubSelected = selectedKey === subKey;

                      return {
                        key: subKey,
                        label: (
                          <span
                            style={{
                              backgroundColor: isSubSelected
                                ? "#FFDC00"
                                : undefined,
                              color: isSubSelected ? "#000" : undefined,
                              fontWeight: isSubSelected ? 500 : undefined,
                              display: "block",
                              padding: "5px 12px",
                              borderRadius: 10,
                            }}
                          >
                            {sub.SubCategoryName}
                          </span>
                        ),
                      };
                    }),
                  }
                : {
                    key: catKey,
                    label: (
                      <span
                        style={{
                          backgroundColor: isCatSelected
                            ? "#FFDC00"
                            : undefined,
                          color: isCatSelected ? "#000" : undefined,
                          fontWeight: isCatSelected ? 500 : undefined,
                          display: "block",
                          padding: "5px 12px",
                          borderRadius: 10,
                        }}
                      >
                        {cat.CategoryName}
                      </span>
                    ),
                  };
            })}
            style={{ width: "auto", marginLeft: -30 }}
          />
        )}

        {isLoading || isPaginationLoading ? (
          <Spin spinning indicator={<LoadingOutlined spin />} />
        ) : (
          <Flex
            vertical
            style={{
              width: "100%",
            }}
          >
            <Flex justify="space-between">
              <Search
                placeholder="Search products..."
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={() => {
                  // console.log(searchValue);

                  getProducts();
                }}
                value={searchValue}
                enterButton
                style={{
                  width: 400,
                  marginBottom: 10,
                }}
              />

              <Select
                value={sortValue}
                defaultValue="date_asc"
                onChange={(value) => {
                  // console.log("Sort value changed:", value);

                  setSortValue(value);
                  getProducts(1, pageSizeVar, value);
                }}
                style={{
                  width: 160,
                  marginRight: isMobile || isTablet ? 0 : "3vw",
                  marginLeft: isMobile ? 10 : 0,
                }}
              >
                <Select.Option value="date_asc">Newest</Select.Option>
                <Select.Option value="date_desc">Oldest</Select.Option>
                <Select.Option value="name_asc">Name ↑ </Select.Option>
                <Select.Option value="name_desc">Name ↓</Select.Option>
              </Select>
            </Flex>

            <List
              ref={containerRef}
              className="productGrid"
              locale={{ emptyText: "No product(s) available." }}
              // grid={{
              //   gutter: 16,
              //   xs: 1, // 1 column on extra small
              //   md: 2, // 2 columns on medium and up
              //   lg: 2, // 2 columns on large
              //   xl: 4, // 4 columns on extra large
              //   xxl: 5, // 5 columns on double extra large
              // }}
              dataSource={products}
              renderItem={(item) => {
                let splitImages = item.ImagesTag.split(";").map((img) =>
                  img.trim()
                );

                return (
                  <List.Item>
                    {(() => {
                      const card = (
                        <Card
                          styles={{
                            body: {
                              paddingTop: 10,
                              paddingBottom: 0,
                              width: "100%",
                              minWidth: 225,
                            },
                            actions: {
                              paddingLeft: 20,
                              paddingRight: 20,
                              paddingTop: 0,
                              paddingBottom: 0,
                              margin: 0,
                              borderTop: "none",
                            },
                          }}
                          style={{
                            cursor: "initial",
                            marginTop: screens.xs ? 10 : 10,
                          }}
                          hoverable={false}
                          cover={
                            <Image
                              width={screens.xs || screens.md ? "100%" : 240}
                              height={200}
                              alt={item.name}
                              style={{ borderRadius: 4, objectFit: "contain" }}
                              src={settings.productImageFolder + splitImages[0]}
                            />
                          }
                          actions={[
                            <Button
                              type="primary"
                              block
                              style={{
                                marginTop: 0,
                                backgroundColor: "#002753",
                                color: "#fff",
                              }}
                              className="view-details-btn"
                              onClick={() => {
                                navigate(
                                  `/product/${toSlug(
                                    item.ProductName
                                  )}/${toSlug(item.Sku)}/${item.Id}`
                                );
                              }}
                            >
                              View Details
                            </Button>,
                          ]}
                        >
                          <Typography.Paragraph
                            ellipsis={{
                              rows: 2,
                              tooltip: item.ProductName,
                            }}
                            style={{
                              width: "100%",
                              minHeight: 50.5,
                              margin: 0,
                              fontSize: 16,
                              color: "#002753",
                              fontWeight: "bold",
                            }}
                          >
                            {item.ProductName}
                          </Typography.Paragraph>

                          <p
                            style={{
                              margin: 0,
                              marginTop: 5,
                              fontSize: 14,
                              color: "#6B7280",
                            }}
                          >
                            {item.Sku}
                          </p>
                        </Card>
                      );

                      // wrap with ribbons
                      let wrapped = card;

                      if (item.Promotions && item.Promotions.length > 0) {
                        wrapped = (
                          <div style={{ position: "relative" }}>
                            <div
                              style={{
                                position: "absolute",
                                top: 10,
                                left: -12,
                                background: "#FFDC00",
                                color: "#fff",
                                padding: "2px 10px",
                                transform: "rotate(-45deg)",
                                fontWeight: "bold",
                                zIndex: 1,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                borderRadius: 4,
                                color: "black",
                              }}
                            >
                              Promo
                              {/* {item.Promotions[0].DiscountPercent}% OFF */}
                            </div>

                            {wrapped}
                          </div>
                        );
                      }

                      if (item.RibbonText) {
                        wrapped = (
                          <Badge.Ribbon text={item.RibbonText} color="red">
                            {wrapped}
                          </Badge.Ribbon>
                        );
                      }

                      return wrapped;
                    })()}
                  </List.Item>
                );
              }}
            />

            <Flex justify="center" style={{ marginTop: 20 }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                //   showSizeChanger={false}
                //   pageSizeOptions: ["6", "10", "20"],
                //   showTotal: (total, range) =>
                //     `${range[0]}-${range[1]} of ${total}`,
                //   onChange: (page, pageSize) => getProducts(page, pageSize),
                //   onShowSizeChange: (page, pageSize) => getProducts(page, pageSize),
                onChange={(page, pageSize) => {
                  updateRouteState({ currentPage: page });

                  // getProducts(page, pageSize);
                }}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Space>
  );
};

export default Products;
